/**
 * Browser state for the tenant-owned simulation revision workflow.
 *
 * This is deliberately separate from the server DTOs. A binding is only a
 * pointer to server-owned project/model/revision records; it is never treated
 * as evidence that a revision was published. Publication succeeds only after
 * the authenticated API returns the new immutable head.
 */
import type { SimulationProjectRole } from '../../../types/simulationProject';

export interface SimulationRevisionConflictState {
  code: 'simulation_revision_conflict';
  expectedBaseRevisionId: string | null;
  actualHeadRevisionId: string | null;
  expectedEtag?: string;
  actualEtag?: string;
  recoveryActions: readonly ('reload' | 'fork' | 'merge')[];
  message: string;
}

export interface SimulationRevisionBinding {
  companyId: string;
  projectId: string;
  modelId: string;
  headRevisionId: string | null;
  headContentHash: string | null;
  /** Normalized physics identity used for run attribution, separate from layout/view content. */
  headNormalizedInputHash: string | null;
  etag: string;
  revisionNumber: number;
  role?: SimulationProjectRole;
  /** Hash of the last recovery draft accepted by the server for this user. */
  draftContentHash?: string | null;
  draftSavedAt?: string | null;
  conflict?: SimulationRevisionConflictState | null;
  /** Stable idempotency key retained across an ambiguous network failure. */
  pendingPublication?: {
    clientMutationId: string;
    contentHash: string;
    baseRevisionId: string | null;
    baseEtag: string;
  } | null;
}

/**
 * Model-mutation authority for a mounted project revision. A conflict is
 * intentionally read-only until the user reloads, forks, or explicitly
 * merges; viewer/reviewer roles may inspect but cannot mutate local revision
 * content that they cannot publish.
 */
export function simulationRevisionMutationBlocker(
  binding: Pick<SimulationRevisionBinding, 'conflict' | 'role'> | null | undefined,
): string | null {
  if (!binding) return null;
  if (binding.conflict) {
    return 'The project head changed. Reload, fork, or merge before editing this revision.';
  }
  if (binding.role !== 'editor' && binding.role !== 'owner') {
    return 'Only project editors and owners may change this simulation revision.';
  }
  return null;
}

export type SimulationPersistencePhase =
  | 'unsaved'
  | 'legacy-personal'
  | 'published'
  | 'modified'
  | 'draft-saved'
  | 'publishing'
  | 'conflict'
  | 'read-only';

export interface SimulationPersistenceState {
  phase: SimulationPersistencePhase;
  label: string;
  detail: string;
  citableRevision: boolean;
  canPublish: boolean;
  canSaveRecoveryDraft: boolean;
}

/**
 * Full immutable-content dirty authority shared by every persisted workbench.
 * A normalized solver-input hash is intentionally insufficient because
 * provenance, accepted assumptions, units, solve membership, analysis setup,
 * and editor view all belong to the revision content contract.
 */
export function simulationRevisionIsDirty(args: {
  headContentHash: string | null | undefined;
  workingContentHash: string | null;
  /** Keeps a user-edited but temporarily invalid/null snapshot dirty. */
  dirtyHint?: boolean;
}): boolean {
  if (args.workingContentHash === null) return args.dirtyHint === true;
  return (
    !args.headContentHash || args.workingContentHash !== args.headContentHash
  );
}

export function isSimulationRevisionBinding(value: unknown): value is SimulationRevisionBinding {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return typeof row.companyId === 'string' && row.companyId.length > 0
    && typeof row.projectId === 'string' && row.projectId.length > 0
    && typeof row.modelId === 'string' && row.modelId.length > 0
    && (row.headRevisionId === null || typeof row.headRevisionId === 'string')
    && (row.headContentHash === null || typeof row.headContentHash === 'string')
    && (row.headNormalizedInputHash === null || typeof row.headNormalizedInputHash === 'string')
    && typeof row.etag === 'string' && /^[a-f0-9]{64}$/.test(row.etag)
    && Number.isInteger(row.revisionNumber) && Number(row.revisionNumber) >= 0;
}

/**
 * Derive factual persistence copy for the editor. Mutable personal documents
 * are explicitly called compatibility records and never described as
 * revisions. A recovery draft is not citable and never advances the head.
 */
export function simulationPersistenceState(args: {
  binding?: SimulationRevisionBinding | null;
  legacyCloudId?: string | null;
  workingContentHash?: string | null;
  publishing?: boolean;
}): SimulationPersistenceState {
  const { binding, legacyCloudId, workingContentHash, publishing = false } = args;
  if (!binding) {
    if (legacyCloudId) {
      return {
        phase: 'legacy-personal',
        label: 'Compatibility save',
        detail: 'Mutable personal document · last write wins · not an immutable revision',
        citableRevision: false,
        canPublish: true,
        canSaveRecoveryDraft: false,
      };
    }
    return {
      phase: 'unsaved',
      label: 'Not published',
      detail: 'Create a tenant project to publish an immutable revision.',
      citableRevision: false,
      canPublish: true,
      canSaveRecoveryDraft: false,
    };
  }

  const canEdit = binding.role === 'editor' || binding.role === 'owner';
  if (!canEdit) {
    return {
      phase: 'read-only',
      label: `Revision ${binding.revisionNumber} · read only`,
      detail: 'This project role cannot publish or overwrite a recovery draft.',
      citableRevision: binding.headRevisionId !== null,
      canPublish: false,
      canSaveRecoveryDraft: false,
    };
  }
  if (binding.conflict) {
    return {
      phase: 'conflict',
      label: 'Revision conflict',
      detail: 'The project head changed. Reload, fork, or explicitly merge before publishing.',
      citableRevision: binding.headRevisionId !== null,
      canPublish: false,
      canSaveRecoveryDraft: true,
    };
  }
  if (publishing) {
    return {
      phase: 'publishing',
      label: 'Publishing…',
      detail: 'Comparing the expected base revision and creating a new immutable head.',
      citableRevision: binding.headRevisionId !== null,
      canPublish: false,
      canSaveRecoveryDraft: true,
    };
  }

  const changed = simulationRevisionIsDirty({
    headContentHash: binding.headContentHash,
    workingContentHash: workingContentHash ?? null,
    // This generic status surface cannot distinguish untouched absence from a
    // failed live normalization, so it remains conservatively dirty.
    dirtyHint: true,
  });
  if (!changed) {
    return {
      phase: 'published',
      label: `Revision ${binding.revisionNumber} · published`,
      detail: 'The complete current revision content matches the immutable project head.',
      citableRevision: binding.headRevisionId !== null,
      canPublish: false,
      canSaveRecoveryDraft: true,
    };
  }
  if (workingContentHash && binding.draftContentHash === workingContentHash) {
    return {
      phase: 'draft-saved',
      label: 'Recovery draft saved',
      detail: `Based on revision ${binding.revisionNumber} · private to you · not a published revision`,
      citableRevision: false,
      canPublish: true,
      canSaveRecoveryDraft: true,
    };
  }
  return {
    phase: 'modified',
    label: 'Unpublished changes',
    detail: `Based on revision ${binding.revisionNumber} · recovery draft pending`,
    citableRevision: false,
    canPublish: true,
    canSaveRecoveryDraft: true,
  };
}
